import React from "react";
import { motion } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import {
  ClockIcon,
  UserIcon,
  FolderIcon,
  ClipboardListIcon,
  CubeIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/outline";

const RecentActivity = ({ activities = [] }) => {
  if (!activities || activities.length === 0) {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Recent Activity
        </h3>
        <div className="text-center py-8">
          <ClockIcon className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
          <p className="text-slate-500 dark:text-slate-400">
            No recent activity
          </p>
        </div>
      </div>
    );
  }

  const getActionIcon = (type) => {
    switch (type) {
      case "project":
        return FolderIcon;
      case "task":
        return ClipboardListIcon;
      case "stock":
        return CubeIcon;
      case "worker":
        return UserIcon;
      case "complete":
        return CheckCircleIcon;
      default:
        return ClockIcon;
    }
  };

  const getActionColor = (type) => {
    switch (type) {
      case "project":
        return "text-blue-500 bg-blue-50 dark:bg-blue-900/20";
      case "task":
        return "text-purple-500 bg-purple-50 dark:bg-purple-900/20";
      case "stock":
        return "text-orange-500 bg-orange-50 dark:bg-orange-900/20";
      case "worker":
        return "text-green-500 bg-green-50 dark:bg-green-900/20";
      case "complete":
        return "text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20";
      default:
        return "text-slate-500 bg-slate-50 dark:bg-slate-700/20";
    }
  };

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
        Recent Activity
      </h3>
      <div className="space-y-3">
        {activities.slice(0, 10).map((activity, index) => {
          const Icon = getActionIcon(activity.type);
          const color = getActionColor(activity.type);

          return (
            <motion.div
              key={activity.id || index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.03 }}
              className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
            >
              <div className={`p-2 rounded-lg ${color} flex-shrink-0`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-900 dark:text-white">
                  {activity.message || activity.action}
                </p>
                {activity.details && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {activity.details}
                  </p>
                )}
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                  {activity.timestamp
                    ? formatDistanceToNow(new Date(activity.timestamp), {
                        addSuffix: true,
                      })
                    : "Just now"}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default RecentActivity;
